import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { chatService, Conversation, Message } from '../../../services/chatService';
import { socketService } from '../../../services/socketService';
import { useToast } from '../../../components/Toast';

export const useChat = (usernameParam?: string) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [onlineStatus, setOnlineStatus] = useState<Record<number, boolean>>({});
    const [lastSeen, setLastSeen] = useState<Record<number, string | undefined>>({});

    // Derived state for sorting or updates
    const [currentTime, setCurrentTime] = useState(Date.now());

    // Socket Connection Management
    useEffect(() => {
        if (!socketService.isConnected()) {
            socketService.connect();
        }

        const handleSocketConnect = () => {
            // Reload conversations to sync status
            setTimeout(() => loadConversations(), 1500);
        };

        const socket = socketService.socket;
        socket?.on('connect', handleSocketConnect);

        // Initial load
        loadConversations();

        // Heartbeat
        const heartbeatInterval = setInterval(() => {
            chatService.sendHeartbeat();
        }, 20000);

        // Time ticker for UI relative times
        const timeInterval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 30000);

        return () => {
            clearInterval(heartbeatInterval);
            clearInterval(timeInterval);
            socket?.off('connect', handleSocketConnect);
        };
    }, []);

    // Socket Event Listeners
    useEffect(() => {
        const unsubscribeNewMessage = chatService.onNewMessage((message: Message) => {
            // Update conversation list
            setConversations(prev => {
                return prev.map(conv => {
                    if (conv.id === message.conversation_id) {
                        return {
                            ...conv,
                            last_message: message,
                            last_message_at: message.created_at,
                            unread_count: (selectedConversation?.id !== message.conversation_id && message.sender_id !== user?.id)
                                ? (conv.unread_count || 0) + 1
                                : conv.unread_count,
                        };
                    }
                    return conv;
                }).sort((a, b) => {
                    const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
                    const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
                    return bTime - aTime;
                });
            });

            // Update messages if selected
            if (selectedConversation && message.conversation_id === selectedConversation.id) {
                setMessages(prev => {
                    if (prev.some(m => m.id === message.id)) return prev;
                    return [...prev, message];
                });
                if (message.sender_id !== user?.id) {
                    chatService.markMessageRead(message.id);
                }
            }
        });

        const unsubscribeConversationUpdate = chatService.onConversationUpdate((conversation: Conversation) => {
            setConversations(prev => {
                const updated = prev.map(conv => {
                    if (conv.id === conversation.id) {
                        // Merge preservation logic from original
                        const existingIsOnline = conv.other_user?.is_online;
                        const updateIsOnline = conversation.other_user?.is_online;
                        const finalIsOnline = updateIsOnline !== undefined ? updateIsOnline : existingIsOnline;

                        const { other_user: _, ...rest } = conversation;
                        return {
                            ...conv,
                            ...rest,
                            other_user: {
                                ...conv.other_user,
                                is_online: finalIsOnline,
                                ...(conversation.other_user?.last_seen !== undefined && { last_seen: conversation.other_user.last_seen })
                            }
                        };
                    }
                    return conv;
                });
                return updated.sort((a, b) => {
                    const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
                    const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
                    return bTime - aTime;
                });
            });
        });

        const unsubscribeUserStatusUpdate = chatService.onUserStatusUpdate((data) => {
            const isOnline = data.is_online === true;
            const userId = Number(data.user_id);

            setOnlineStatus(prev => ({ ...prev, [userId]: isOnline }));
            setLastSeen(prev => ({ ...prev, [userId]: data.last_seen }));

            setConversations(prev => prev.map(conv => {
                if (Number(conv.other_user.id) === userId) {
                    return {
                        ...conv,
                        other_user: {
                            ...conv.other_user,
                            is_online: isOnline,
                            last_seen: data.last_seen
                        }
                    };
                }
                return conv;
            }));

            if (selectedConversation && Number(selectedConversation.other_user.id) === userId) {
                setSelectedConversation(prev => prev ? ({
                    ...prev,
                    other_user: {
                        ...prev.other_user,
                        is_online: isOnline,
                        last_seen: data.last_seen
                    }
                }) : null);
            }
        });

        return () => {
            unsubscribeNewMessage();
            unsubscribeConversationUpdate();
            unsubscribeUserStatusUpdate();
        };
    }, [selectedConversation, user?.id]);


    const loadConversations = async () => {
        try {
            setLoading(true);
            const data = await chatService.getConversations();
            if (Array.isArray(data)) {
                // Ensure is_online boolean
                const cleaned = data.map(c => ({
                    ...c,
                    other_user: {
                        ...c.other_user,
                        is_online: c.other_user?.is_online === true
                    }
                }));
                setConversations(cleaned);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const loadMessages = async (conversationId: number) => {
        try {
            setLoadingMessages(true);
            const data = await chatService.getMessages(conversationId);
            setMessages(data.messages);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingMessages(false);
        }
    };

    // Selecting Conversation Logic
    const selectConversation = useCallback((conversation: Conversation) => {
        setSelectedConversation(conversation);
        loadMessages(conversation.id);

        // Join socket room
        if (socketService.isConnected()) {
            chatService.joinConversation(conversation.id);
            chatService.markAllRead(conversation.id);
        }

        // Update URL
        navigate(`/chat/${conversation.other_user.username}`, { replace: true });
    }, [navigate]);

    // Handle Username Param
    useEffect(() => {
        if (usernameParam && usernameParam !== user?.username) {
            // Find in existing
            const existing = conversations.find(c => c.other_user.username === usernameParam);
            if (existing) {
                if (selectedConversation?.id !== existing.id) {
                    selectConversation(existing);
                }
            } else {
                // Or create new
                // This logic might need userService to get ID from username, 
                // but chatService.createOrGetConversation usually takes ID.
                // We might need to fetch user profile first if not in list.
            }
        }
    }, [usernameParam, conversations, user?.username, selectConversation, selectedConversation]);

    const sendMessage = async (content: string, files?: { file?: File, video?: File, link?: string }) => {
        if (!selectedConversation) return;
        try {
            const newMessage = await chatService.sendMessage(
                selectedConversation.id,
                content,
                {
                    attachment: files?.file,
                    video: files?.video,
                    linkUrl: files?.link
                }
            );
            // Optimistic add
            setMessages(prev => [...prev, newMessage]);
            return newMessage;
        } catch (error) {
            console.error(error);
            showToast('Failed to send message', 'error');
            throw error;
        }
    };

    return {
        conversations,
        selectedConversation,
        messages,
        loading,
        loadingMessages,
        selectConversation,
        sendMessage,
        loadConversations,
        onlineStatus,
        currentTime
    };
};
